<template>
  <div v-if="hasOpenedAtleastOnce" class="dashboard-app--container">
    <div
      v-for="(configItem, index) in config"
      :key="index"
      class="dashboard-app--list"
    >
      <loading-state
        v-if="isIframeLoading(index)"
        :message="$t('EMBED_VIEW.LOADING')"
        class="dashboard-app_loading-container"
      />
      <iframe
        v-else-if="
          configItem.type === 'frame' &&
            configItem.url &&
            isConfigEmbeddable(index)
        "
        :ref="`dashboardAppFrame-${index}`"
        :key="getFrameKey(index)"
        :src="configItem.url"
        @load="() => onIframeLoad(index)"
      />
      <div v-else class="dashboard-app_loading-container">
        <h2>{{ $t('EMBED_VIEW.UNABLE_TO_DISPLAY_TITLE') }}</h2>
        <p>{{ getErrorMessage(index) || $t('EMBED_VIEW.NOT_EMBEDDABLE') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingState from 'dashboard/components/widgets/LoadingState';
export default {
  components: {
    LoadingState,
  },
  props: {
    config: {
      type: Array,
      default: () => [],
    },
    currentChat: {
      type: Object,
      default: () => ({}),
    },
    isVisible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hasOpenedAtleastOnce: false,
      iframeLoading: true,
      iframeLoadingMap: {},
      isEmbeddableMap: {},
      errorMessageMap: {},
      frameKeyMap: {},
    };
  },
  computed: {
    dashboardAppContext() {
      return {
        conversation: this.currentChat,
        contact: this.$store.getters['contacts/getContact'](this.contactId),
        currentAgent: this.currentAgent,
      };
    },
    contactId() {
      return this.currentChat?.meta?.sender?.id;
    },
    currentAgent() {
      const { id, name, email } = this.$store.getters.getCurrentUser;
      return { id, name, email };
    },
  },
  watch: {
    isVisible() {
      if (this.isVisible) {
        this.hasOpenedAtleastOnce = true;
        this.validateAllFrames();
      }
    },
    config: {
      deep: true,
      handler() {
        if (this.isVisible) {
          this.validateAllFrames();
        }
      },
    },
  },

  mounted() {
    window.addEventListener('message', this.onWindowMessage);
    if (this.isVisible) {
      this.hasOpenedAtleastOnce = true;
      this.validateAllFrames();
    }
  },
  beforeDestroy() {
    window.removeEventListener('message', this.onWindowMessage);
  },
  methods: {
    isIframeLoading(index) {
      return this.iframeLoadingMap[index] ?? false;
    },
    isConfigEmbeddable(index) {
      return this.isEmbeddableMap[index] !== false;
    },
    getErrorMessage(index) {
      return this.errorMessageMap[index] || '';
    },
    getFrameKey(index) {
      return this.frameKeyMap[index] || 0;
    },
    getFrameElement(index) {
      const frameRef = this.$refs[`dashboardAppFrame-${index}`];
      return Array.isArray(frameRef) ? frameRef[0] : frameRef;
    },
    findFrameIndexBySource(sourceWindow) {
      return this.config.findIndex((configItem, index) => {
        if (configItem?.type !== 'frame' || !configItem?.url) {
          return false;
        }
        const frameElement = this.getFrameElement(index);
        return frameElement?.contentWindow === sourceWindow;
      });
    },
    onWindowMessage(event) {
      if (typeof event.data !== 'string') {
        return;
      }

      if (event.data !== 'chatwoot-dashboard-app:fetch-info') {
        return;
      }

      const frameIndex = this.findFrameIndexBySource(event.source);
      if (frameIndex === -1) {
        return;
      }

      this.onIframeLoad(frameIndex);
    },
    async validateAndShow(index, url) {
      this.$set(this.iframeLoadingMap, index, true);
      try {
        const { embeddable } = await this.$store.dispatch(
          'dashboardApps/checkEmbeddable',
          url
        );
        const isEmbeddable = !!embeddable;
        this.$set(this.isEmbeddableMap, index, isEmbeddable);
        if (!isEmbeddable) {
          this.$set(
            this.errorMessageMap,
            index,
            this.$t('EMBED_VIEW.NOT_EMBEDDABLE')
          );
        } else {
          this.$set(this.errorMessageMap, index, '');
          const currentKey = this.frameKeyMap[index] || 0;
          this.$set(this.frameKeyMap, index, currentKey + 1);
        }
      } catch (error) {
        this.$set(this.isEmbeddableMap, index, false);
        this.$set(
          this.errorMessageMap,
          index,
          error?.message || this.$t('EMBED_VIEW.ERROR_CHECKING_URL')
        );
      } finally {
        this.$set(this.iframeLoadingMap, index, false);
      }
    },
    validateAllFrames() {
      this.config.forEach((configItem, index) => {
        if (configItem?.type === 'frame' && configItem?.url) {
          this.validateAndShow(index, configItem.url);
        }
      });
    },
    onIframeLoad(index) {
      const frameElement = this.getFrameElement(index);
      if (frameElement && frameElement.contentWindow) {
        const eventData = {
          event: 'appContext',
          data: this.dashboardAppContext,
        };
        frameElement.contentWindow.postMessage(JSON.stringify(eventData), '*');
      }
      this.$set(this.iframeLoadingMap, index, false);
    },
  },
};
</script>

<style scoped>
.dashboard-app--container,
.dashboard-app--list,
.dashboard-app--list iframe {
  height: 100%;
  width: 100%;
}

.dashboard-app--list iframe {
  border: 0;
}
.dashboard-app_loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  flex-direction: column;
  text-align: center;
  gap: 0.25rem;
}
.dashboard-app_loading-container h2 {
  font-size: 1.5rem;
  line-height: 1.25;
  margin: 0.25rem 0 0;
}
.dashboard-app_loading-container p {
  margin: 0.25rem 0 0;
}
</style>
